import type { CSSProperties } from 'react';
import { cn } from '@/lib/cn';

interface ColorSwatchProps {
  hex: string;
  /** Diameter in px. Default 12. */
  size?: number;
  className?: string;
}

/**
 * Project colour dot. Project colours are data, so they are applied inline (the only place
 * besides SwatchPicker); the ring keeps light colours visible on white (§10.6).
 */
export function ColorSwatch({ hex, size = 12, className }: ColorSwatchProps): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block shrink-0 rounded-full ring-1 ring-border', className)}
      style={{ backgroundColor: hex, width: size, height: size }}
    />
  );
}

/**
 * Entry row colour bar: 2 px wide (original EntryCard, sprint 7), full height. The project colour is inline data like
 * ColorSwatch; with no project it falls back to a muted token class.
 */
export function ProjectColorBar({ hex, className }: { hex?: string | null; className?: string }): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={cn('w-0.5 shrink-0 self-stretch rounded-full', !hex && 'bg-muted-foreground/40', className)}
      style={hex ? { backgroundColor: hex } : undefined}
    />
  );
}

/**
 * Original project-card accent: a 4 px left border in the project colour. Use only on elements that
 * already carry .card, whose border class supplies the solid style.
 */
export function projectBorderStyle(hex: string): CSSProperties {
  return { borderLeftColor: hex, borderLeftWidth: 4 };
}
